import { z } from 'zod';

import { ValidEnv } from './routes';

export const csrfCookieName = '_csrf';
export const csrfMismatchMessage = 'CSRF token mismatch';
export const csrfTokenMissingMessage = 'CSRF token missing';
export const wosSessionCookieName = 'workos-session';
/**
 * Tab-scoped guest session credential. Stored in sessionStorage (not a
 * Set-Cookie) and sent explicitly via header / websocket params so other
 * browser tabs keep using WorkOS. Used for external magic-link access
 * (signing, assigned tasks, collab drop-ins, etc.).
 */
export const guestSessionStorageKey = 'aline-guest-session';
export const guestSessionHeaderName = 'x-aline-guest-session';
/**
 * Short-lived one-time code on magic-link redirects. Redeemed once for the
 * tab-scoped guest session bearer — never put the bearer itself in the URL.
 */
export const guestExchangeQueryKey = 'guestCode';
/** AuthKit callback redirect flag so only that tab clears its guest session. */
export const clearGuestSessionQueryKey = 'clearGuestSession';

export const WOSSessionConnectionParamsSchema = z.object({
  __typename: z.literal('WorkOSSessionCookie'),
  cookie: z.string(),
});
export const GuestSessionTokenConnectionParamsSchema = z.object({
  __typename: z.literal('GuestSessionToken'),
  token: z.string(),
});
export const ServerJWTConnectionParamsSchema = z.object({
  __typename: z.literal('ServerJWT'),
  token: z.string(),
});
export const ApiKeyBearerTokenSchema = z.object({
  __typename: z.literal('ApiKeyBearerToken'),
  token: z.string(),
});
/**
 * Browser session connection params (guest token or WorkOS cookie). Narrower
 * than the full union, deliberately excludes ServerJWT so functions that only
 * handle the browser path can express that in their signature.
 */
export const CookieConnectionParamsSchema = z.union([
  GuestSessionTokenConnectionParamsSchema,
  WOSSessionConnectionParamsSchema,
]);
export const WebsocketConnectionParamsSchema = z.union([
  CookieConnectionParamsSchema,
  ServerJWTConnectionParamsSchema,
  ApiKeyBearerTokenSchema,
]);
export const SubscriptionExtensionsSchema = z
  .object({
    auth: WebsocketConnectionParamsSchema.optional(),
  })
  .optional();
export const RedirectRouteSchema = z.object({
  pageLoad: z.boolean(),
  route: z.string(),
});
export const RedirectURISchema = z.object({
  redirectUri: RedirectRouteSchema,
});
export const RedirectURIOptionalSchema = RedirectURISchema.extend({
  redirectUri: RedirectRouteSchema.optional(),
});
export const PreLoginRedirectSchema = z.object({
  email: z.string(),
  redirectPath: z.string(),
});
export const WorkOSAuthCheckSchema = z.object({
  redirectPath: z.string(),
});
export const CILoginBodySchema = z.object({
  email: z.string(),
});
export const CSRFResponseSchema = z.object({
  csrfToken: z.string(),
});
export const GuestSessionExchangeBodySchema = z.object({
  code: z.string().min(1),
});
export const GuestSessionExchangeResponseSchema = z.object({
  guestSessionToken: z.string().min(1),
});
export const ConfigureSSOBodySchema = z.object({
  alineOrgId: z.string(),
});
export const EnableDomainVerificationBodySchema = z.object({
  alineOrgId: z.string(),
});

export function getIsCSRFEnabled(env: ValidEnv) {
  return process.env.NODE_ENV !== 'test' && env !== 'ci';
}

export function shouldSecureCookies(env: ValidEnv) {
  return env !== ValidEnv.dev && env !== ValidEnv.ci;
}

export type CILoginBody = z.infer<typeof CILoginBodySchema>;
export type RedirectURI = z.infer<typeof RedirectURISchema>;
export type CSRFResponse = z.infer<typeof CSRFResponseSchema>;
export type WorkOSAuthCheck = z.infer<typeof WorkOSAuthCheckSchema>;
export type PreLoginRedirect = z.infer<typeof PreLoginRedirectSchema>;
export type ConfigureSSOBody = z.infer<typeof ConfigureSSOBodySchema>;
export type RedirectURIOptional = z.infer<typeof RedirectURIOptionalSchema>;
export type CookieConnectionParams = z.infer<typeof CookieConnectionParamsSchema>;
export type ApiKeyBearerToken = z.infer<typeof ApiKeyBearerTokenSchema>;
export type WebsocketConnectionParams = z.infer<typeof WebsocketConnectionParamsSchema>;
export type SubscriptionExtensions = z.infer<typeof SubscriptionExtensionsSchema>;
export type GuestSessionExchangeBody = z.infer<typeof GuestSessionExchangeBodySchema>;
export type GuestSessionExchangeResponse = z.infer<typeof GuestSessionExchangeResponseSchema>;
export type EnableDomainVerificationBody = z.infer<typeof EnableDomainVerificationBodySchema>;
